// ---------------------------------------------------------------------------
// property-clock.ts — what time is it AT THE HOTEL?
// ---------------------------------------------------------------------------
//
// Schedules are the hotel's, not the viewer's. A manager checking the lobby
// playlist from another country, or a laptop whose OS timezone is simply wrong,
// must not shift when the music changes. `TenantProperty.timezone` is the
// authority; `new Date().getHours()` is not.
//
// Kept apart from schedule.ts so that file can stay free of `Date` and `Intl`
// and remain testable by passing numbers in.
// ---------------------------------------------------------------------------

import { DAYS_PER_WEEK, MINUTES_PER_DAY } from './schedule';

export interface PropertyTime {
  /** 0..1439 */
  minuteOfDay: number;
  /** 0 = Sunday */
  weekday: number;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/**
 * Cache of formatters.
 *
 * `Intl.DateTimeFormat` construction is the expensive part, and this is called
 * on every schedule re-evaluation. Keyed by timezone; a property's timezone
 * changes approximately never, so the map stays tiny.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string): Intl.DateTimeFormat {
  const cached = formatters.get(timeZone);
  if (cached) return cached;

  // `hourCycle: 'h23'` matters: the default for some locales is h12, and others
  // render midnight as hour 24, either of which would produce a wrong
  // minute-of-day. Fixing the locale to en-GB keeps the parts predictable
  // regardless of the user's display language — this value is never shown.
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  formatters.set(timeZone, fmt);
  return fmt;
}

/**
 * Read the clock at the property.
 *
 * An unknown or malformed timezone makes `Intl` throw. Rather than break the
 * player, that falls back to the browser's own clock and the caller carries on —
 * a schedule an hour out is a far better outcome than a music panel that will
 * not render. A bad timezone is a data problem to fix in property settings, not
 * a reason to lose playback.
 */
export function propertyNow(timeZone: string | null | undefined, now: Date = new Date()): PropertyTime {
  if (timeZone) {
    try {
      const parts = formatterFor(timeZone).formatToParts(now);
      const lookup = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find((p) => p.type === type)?.value;

      const weekdayName = lookup('weekday');
      const hour = Number(lookup('hour'));
      const minute = Number(lookup('minute'));

      const weekday = weekdayName === undefined ? undefined : WEEKDAY_INDEX[weekdayName];

      if (weekday !== undefined && Number.isFinite(hour) && Number.isFinite(minute)) {
        return {
          // hourCycle h23 gives 0..23, but guard anyway: a 24 here would produce
          // minuteOfDay 1440, which no window can ever match.
          minuteOfDay: (hour * 60 + minute) % MINUTES_PER_DAY,
          weekday: weekday % DAYS_PER_WEEK,
        };
      }
    } catch {
      // Fall through to the local clock.
    }
  }

  return {
    minuteOfDay: now.getHours() * 60 + now.getMinutes(),
    weekday: now.getDay(),
  };
}

/** Milliseconds until the start of the next minute — used to align wake-ups. */
export function msToNextMinute(now: Date = new Date()): number {
  return 60_000 - (now.getSeconds() * 1000 + now.getMilliseconds());
}
